import { AccordionSection } from './AccordionSection/AccordionSection'
import { AuthSection } from '@app/code/src/components/LoggedOutCodeGeneratorV2/GeneratorSections/AuthSection/AuthSection'
import { Box, Chip, Stack, Typography } from '@dtx-company/design-system/src'
import { CodeConfigurationField } from '@dtx-company/inter-app/src/types/flowcode'
import { CodePreview } from '@app/code/src/components/LoggedOutCodeGeneratorV2/CodePreview/CodePreview'
import { FC, useState } from 'react'
import { LoggedOutGeneratorSection } from '@app/code/src/components/LoggedOutCodeGeneratorV2/LoggedOutCodeGenerator.types'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { selectError, selectScanDestinationType } from '@app/code/src/machines/locg/locgSelectors'
import { useGlobalServices } from '@app/common/src/hooks/useGlobalServices'
import { useSelector } from '@xstate/react'
import AllInclusiveIcon from '@mui/icons-material/AllInclusive'
import useTranslation from 'next-translate/useTranslation'

export const AccordionLOCGenerator: FC = () => {
  const [expanded, setExpanded] = useState<LoggedOutGeneratorSection | null>(
    LoggedOutGeneratorSection.ScanDestination
  )
  const { locgService } = useGlobalServices()

  const { t } = useTranslation('logged-out-generator')
  const selectedScanDestinationType = useSelector(locgService, selectScanDestinationType)
  const scanDestinationError = useSelector(
    locgService,
    selectError
  )(CodeConfigurationField.SCAN_DESTINATION)
  const onAccordionChange = (expanded: boolean, section: LoggedOutGeneratorSection): void => {
    fireAnalyticsEvent('LoggedOutGenerator_Accordion_Change', { section, expanded })
    setExpanded(expanded ? section : null)
  }

  const onSubmitError = (): void => {
    if (scanDestinationError) {
      onAccordionChange(true, LoggedOutGeneratorSection.ScanDestination)
    }
  }

  return (
    <Stack gap={4}>
      <Stack direction={'row'} justifyContent={'space-between'} sx={{ my: 2 }}>
        <Typography
          component="h1"
          variant={{ xs: 'subtitleMedium', md: 'headlineMedium', lg: 'headlineLarge' }}
          data-testid="desktop-title"
        >
          {t('Header.title')}
        </Typography>
        <Chip
          component="a"
          href={
            'https://help.flowcode.com/en/articles/6927454-is-flowcode-qr-free-is-there-a-scan-limit?utm_source=LOCG&utm_medium=Website&utm_campaign=&utm_content=chip_2&utm_term=unlimited_scans'
          }
          target="_blank"
          variant="outlined"
          startIcon={AllInclusiveIcon}
          label={t('Header.chip')}
          data-testid="heading-chip"
          data-heap-id="LoggedOutGenerator_Heading_MarketingChip"
          aria-label={t('LoggedOutGenerator.MarketingChip.ariaLabel', {
            text: t('Header.chip')
          })}
        />
      </Stack>
      <Stack
        sx={theme => ({
          display: 'grid',
          gridTemplateColumns: { sx: '1fr', md: '5fr 6fr' },
          gap: '133px',
          position: 'relative',
          overflowY: 'hidden',
          height: `auto`,
          width: '100%',
          gridTemplateRows: `minmax(${theme.spacing(140)}, 80vh)`,
          p: 2
        })}
      >
        <Stack sx={{ height: '100%', overflowY: 'auto' }}>
          <AccordionSection
            expanded={expanded}
            onAccordionChange={onAccordionChange}
            selectedScanDestinationType={selectedScanDestinationType}
          />
        </Stack>
        <Stack
          gap={2}
          sx={{ display: 'grid', gridTemplateRows: '1fr max-content ', position: 'relative' }}
        >
          <CodePreview accordionPreviewStyle />
          <Box sx={theme => ({ height: { lg: theme.spacing(86.5) } })}>
            <AuthSection hideBackButton onSubmitError={onSubmitError} />
          </Box>
        </Stack>
      </Stack>
    </Stack>
  )
}
